// 096: the house dialog, once. A small modal on the `.ap-modal` idiom:
// backdrop, card, an eyebrow and a title, the close control, and the
// caller's body. Escape and the backdrop close it. It renders through a
// portal onto the document body, so a dialog opened from inside the
// shell's sidebar (a sticky, stacked column) still sits over the whole
// screen; its positioning is self-contained so it does not depend on any
// other stylesheet being loaded first.

import { useEffect, useId, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import '../styles/ShellDialog.css';

export interface ShellDialogProps {
  title: string;
  eyebrow?: string;
  onClose: () => void;
  children: ReactNode;
  className?: string;
}

export default function ShellDialog({ title, eyebrow, onClose, children, className }: ShellDialogProps) {
  const titleId = useId();
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const dialog = (
    <div className="ap-modal-backdrop shell-dialog-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className={`ap-modal shell-dialog${className ? ` ${className}` : ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <button type="button" className="shell-dialog-close" aria-label="Close" onClick={onClose}>
          <X size={16} aria-hidden="true" />
        </button>
        {eyebrow ? <div className="shell-dialog-eyebrow">{eyebrow}</div> : null}
        <h2 id={titleId} className="shell-dialog-title">{title}</h2>
        {children}
      </div>
    </div>
  );

  return typeof document === 'undefined' ? dialog : createPortal(dialog, document.body);
}
